import { useMutation, useQuery } from '@apollo/client/react';
import { Accordion, Box, Center, ProgressCircle, Heading, HStack, Icon, Spinner, Text, VStack, AbsoluteCenter } from '@chakra-ui/react';
import { AppLayoutContextValue, useParentAppLayout } from '@src/components/sco/AppLayout/AppLayoutContext';
import { AppPanelToggleButton } from '@src/components/sco/AppLayout/Panel/AppPanelToggleButton';
import { pitchBuilderSections } from '@src/components/sco/InvestorMatching/InvestorMatchPitchBuilder/FormSections';
import { PITCH_BUILDER_INVESTOR_SEARCH_FRAGMENT } from '@src/components/sco/InvestorMatching/InvestorMatchPitchBuilder/index';
import { useBreakpointContext } from '@src/components/sco/utilities/BreakpointProvider';
import { gql } from '@src/lib/gql';
import React, { useState } from 'react';
import { LuCheckCheck } from 'react-icons/lu';
import { CheckIcon } from '../../icons';
export const PitchBuilderForms: React.FC<{
  investorMatchAppLayout?: AppLayoutContextValue;
  investorSearchId: string;
}> = props => {
  const {
    getBreakpointValue
  } = useBreakpointContext();
  const pitchBuilderLayout = useParentAppLayout();
  const [activeIndex, setActiveIndex] = useState<number>(0);
  const query = useQuery(gql`
      query ($investorSearchId: String!) {
        scoInvestorSearch(investorSearchId: $investorSearchId) {
          ${PITCH_BUILDER_INVESTOR_SEARCH_FRAGMENT}
        }
      }
    `, {
    variables: {
      investorSearchId: props.investorSearchId
    }
  });
  const [upsertInvestorSearch, upsertInvestorSearchMutation] = useMutation(gql`
    mutation ($input: UpsertInvestorSearchInput!) {
      upsertInvestorSearch(input: $input) {
        investorSearch {
          ${PITCH_BUILDER_INVESTOR_SEARCH_FRAGMENT}
        }
      }
    }
  `);
  const investorSearch = query.data?.scoInvestorSearch;
  if (query.loading && !investorSearch) {
    return <Center padding={4} width={'full'}>
        <Spinner />
      </Center>;
  }
  if (query.error) {
    return <Center padding={4} width={'full'}>
        <Text>Error: ${query.error.toString()}</Text>
      </Center>;
  }
  if (!investorSearch) {
    return <Center>Not Loaded</Center>;
  }
  return <VStack align={'stretch'} gap={0} height={'full'} justify={'stretch'} width={'full'}>
      <HStack bgColor={'colorPalette.bg'} height={'3.5rem'} justify={'space-between'} paddingX={3}>
        <HStack>
          <AppPanelToggleButton appLayout={props.investorMatchAppLayout} panelId={'investor-matching-nav'} panelPosition={'left'} />
          <Heading size={'sm'}>Pitch Builder</Heading>
        </HStack>
        <HStack color={'colorPalette.border'} gap={2}>
          {upsertInvestorSearchMutation.loading ? <>
              <Spinner color={'colorPalette.border'} />
              <Text fontSize={'xs'} textStyle={'label'}>
                Saving
              </Text>
            </> : <>
              <Icon asChild>
                <LuCheckCheck />
              </Icon>
              <Text fontSize={'xs'} textStyle={'label'}>
                Saved
              </Text>
            </>}
          <AppPanelToggleButton appLayout={pitchBuilderLayout} panelId={'pitch-builder-preview'} panelPosition={'right'} />
        </HStack>
      </HStack>
      <Box flex={1} overflow={'scroll'} padding={2}>
        <Accordion.Root bgColor={'transparent'} value={[activeIndex.toString()]} onValueChange={({
        value
      }) => {
        const newVal = value?.[0];
        if (newVal != null) {
          setActiveIndex(parseInt(newVal, 10));
        }
      }}>
          {pitchBuilderSections.map((section, index) => {
          const progress = section.getProgress(investorSearch);
          const Component = section.FormComponent;
          return <Accordion.Item key={index} bgColor={activeIndex === index ? 'colorPalette.container' : 'colorPalette.container/50'} border={activeIndex === index ? '1px solid' : 'none'} borderColor={'colorPalette.focusRing'} borderRadius={'md'} marginBottom={2} value={index.toString()}>
                <Accordion.ItemTrigger borderBottom={activeIndex === index ? '1px solid' : 'none'} borderBottomRadius={activeIndex === index ? 'none' : 'lg'} borderColor={'colorPalette.border/50'} padding={4}>
                  <HStack align={'center'} as={'span'} gap={3} width={'full'}>
                    <ProgressCircle.Root colorPalette={'teal'} size={'sm'} strokeLinecap={'round'} value={progress.percentComplete}>
                      <ProgressCircle.Circle css={{
                    '--thickness': '0.25em'
                  }}>
                        <ProgressCircle.Track />
                        <ProgressCircle.Range />
                      </ProgressCircle.Circle>
                      <AbsoluteCenter>
                        {progress.percentComplete === 100 ? <Icon as={CheckIcon} color={'colorPalette.solid'} /> : <Text fontSize={'2xs'} textStyle={'label'}>
                            {progress.completedSteps}/{progress.totalSteps}
                          </Text>}
                      </AbsoluteCenter>
                    </ProgressCircle.Root>
                    <Heading color={'colorPalette.fg'} size={'md'}>
                      {section.title}
                    </Heading>
                  </HStack>
                  <Accordion.ItemIndicator />
                </Accordion.ItemTrigger>
                <Accordion.ItemContent>
                  <Accordion.ItemBody>
                    <Component investorSearch={investorSearch} upsertInvestorSearch={upsertInvestorSearch} upsertInvestorSearchMutation={upsertInvestorSearchMutation} onNextSection={() => {
                  setActiveIndex(activeIndex + 1);
                }} onPrevSection={() => {
                  setActiveIndex(activeIndex - 1);
                }} />
                  </Accordion.ItemBody>
                </Accordion.ItemContent>
              </Accordion.Item>;
        })}
        </Accordion.Root>
      </Box>
    </VStack>;
};